import { Slot, Slottable } from "@radix-ui/react-slot"
import { type VariantProps, cva } from "class-variance-authority"
import {
  type CSSProperties,
  type ComponentProps,
  type PointerEvent,
  useState,
} from "react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-none text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "group/btn relative isolate overflow-visible text-[oklch(0.95_0.02_145)] border border-[oklch(0.38_0.12_145)] disabled:opacity-100 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:border-border",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "group/btn relative isolate overflow-visible bg-none text-foreground border border-border-heavy",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        gold: "group/btn relative isolate overflow-visible font-display font-bold tracking-wider uppercase border border-[oklch(0.55_0.12_85)]",
        fire: "bg-none text-muted-foreground border border-border shrink-0 hover:text-destructive hover:border-[oklch(0.4_0.15_28)] hover:bg-[oklch(0.22_0.06_28/0.3)]",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-none gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-none px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>

/* ── Inline styles per variant (gradients + shadows too complex for Tailwind) ── */

const variantBaseStyles: Partial<Record<ButtonVariant, CSSProperties>> = {
  default: {
    background:
      "linear-gradient(180deg, oklch(0.55 0.18 145), oklch(0.42 0.15 145))",
    boxShadow:
      "inset 0 1px 0 oklch(0.65 0.2 145 / 0.4), 0 2px 4px oklch(0 0 0 / 0.4)",
    textShadow: "0 1px 1px oklch(0 0 0 / 0.3)",
    transition: "all 0.15s",
  },
  secondary: {
    background: "none",
    transition: "all 0.15s",
  },
  gold: {
    background:
      "linear-gradient(180deg, oklch(0.72 0.16 85), oklch(0.58 0.14 85))",
    color: "oklch(0.13 0.01 55)",
    boxShadow:
      "inset 0 1px 0 oklch(0.8 0.18 85 / 0.4), 0 2px 4px oklch(0 0 0 / 0.4)",
    textShadow: "0 1px 1px oklch(0 0 0 / 0.2)",
    transition: "all 0.15s",
  },
  fire: {
    transition: "all 0.15s",
  },
}

const variantHoverStyles: Partial<Record<ButtonVariant, CSSProperties>> = {
  default: {
    background:
      "linear-gradient(180deg, oklch(0.6 0.2 145), oklch(0.46 0.17 145))",
    boxShadow:
      "inset 0 1px 0 oklch(0.65 0.2 145 / 0.4), 0 2px 4px oklch(0 0 0 / 0.4)",
  },
  gold: {
    background:
      "linear-gradient(180deg, oklch(0.76 0.18 85), oklch(0.62 0.16 85))",
    boxShadow:
      "inset 0 1px 0 oklch(0.8 0.18 85 / 0.4), 0 0 12px oklch(0.7 0.15 85 / 0.4), 0 2px 4px oklch(0 0 0 / 0.4)",
  },
}

const variantActiveStyles: Partial<Record<ButtonVariant, CSSProperties>> = {
  default: {
    background:
      "linear-gradient(180deg, oklch(0.42 0.15 145), oklch(0.48 0.17 145))",
    boxShadow: "inset 0 1px 3px oklch(0 0 0 / 0.4)",
  },
  gold: {
    background:
      "linear-gradient(180deg, oklch(0.58 0.14 85), oklch(0.65 0.16 85))",
    boxShadow: "inset 0 1px 3px oklch(0 0 0 / 0.4)",
  },
}

const variantDisabledStyles: Partial<Record<ButtonVariant, CSSProperties>> = {
  default: {
    background:
      "linear-gradient(180deg, oklch(0.28 0.04 55), oklch(0.22 0.03 55))",
    boxShadow: "none",
    textShadow: "none",
  },
}

/* ── Neon bar configs (replaces ::before/::after pseudo-elements) ── */

interface NeonBarConfig {
  outer: CSSProperties
  inner: CSSProperties
}

const neonBarConfigs: Partial<Record<ButtonVariant, NeonBarConfig>> = {
  default: {
    outer: {
      inset: "-6px",
      background: [
        "linear-gradient(oklch(0.8 0.28 145 / 0.5), oklch(0.8 0.28 145 / 0.5)) left top 10px / 1px calc(100% - 20px) no-repeat",
        "linear-gradient(oklch(0.8 0.28 145 / 0.5), oklch(0.8 0.28 145 / 0.5)) left 10px bottom / calc(100% - 20px) 1px no-repeat",
        "linear-gradient(oklch(0.8 0.28 145 / 0.5), oklch(0.8 0.28 145 / 0.5)) right top 10px / 1px calc(100% - 20px) no-repeat",
      ].join(", "),
    },
    inner: {
      inset: "-3px",
      background: [
        "linear-gradient(oklch(0.8 0.28 145), oklch(0.8 0.28 145)) left top 5px / 1px calc(100% - 10px) no-repeat",
        "linear-gradient(oklch(0.8 0.28 145), oklch(0.8 0.28 145)) left 5px bottom / calc(100% - 10px) 1px no-repeat",
        "linear-gradient(oklch(0.8 0.28 145), oklch(0.8 0.28 145)) right top 5px / 1px calc(100% - 10px) no-repeat",
      ].join(", "),
    },
  },
  secondary: {
    outer: {
      inset: "-6px",
      background: [
        "linear-gradient(oklch(0.8 0.28 85 / 0.5), oklch(0.8 0.28 85 / 0.5)) left top 10px / 1px calc(100% - 20px) no-repeat",
        "linear-gradient(oklch(0.8 0.28 85 / 0.5), oklch(0.8 0.28 85 / 0.5)) left 10px bottom / calc(100% - 20px) 1px no-repeat",
        "linear-gradient(oklch(0.8 0.28 85 / 0.5), oklch(0.8 0.28 85 / 0.5)) right top 10px / 1px calc(100% - 20px) no-repeat",
      ].join(", "),
    },
    inner: {
      inset: "-3px",
      background: [
        "linear-gradient(oklch(0.8 0.28 85), oklch(0.8 0.28 85)) left top 5px / 1px calc(100% - 10px) no-repeat",
        "linear-gradient(oklch(0.8 0.28 85), oklch(0.8 0.28 85)) left 5px bottom / calc(100% - 10px) 1px no-repeat",
        "linear-gradient(oklch(0.8 0.28 85), oklch(0.8 0.28 85)) right top 5px / 1px calc(100% - 10px) no-repeat",
      ].join(", "),
    },
  },
  gold: {
    outer: {
      inset: "-6px",
      background: [
        "linear-gradient(oklch(0.8 0.28 85 / 0.5), oklch(0.8 0.28 85 / 0.5)) left top 10px / 1px calc(100% - 20px) no-repeat",
        "linear-gradient(oklch(0.8 0.28 85 / 0.5), oklch(0.8 0.28 85 / 0.5)) left 10px bottom / calc(100% - 20px) 1px no-repeat",
        "linear-gradient(oklch(0.8 0.28 85 / 0.5), oklch(0.8 0.28 85 / 0.5)) right top 10px / 1px calc(100% - 20px) no-repeat",
      ].join(", "),
    },
    inner: {
      inset: "-3px",
      background: [
        "linear-gradient(oklch(0.8 0.28 85), oklch(0.8 0.28 85)) left top 5px / 1px calc(100% - 10px) no-repeat",
        "linear-gradient(oklch(0.8 0.28 85), oklch(0.8 0.28 85)) left 5px bottom / calc(100% - 10px) 1px no-repeat",
        "linear-gradient(oklch(0.8 0.28 85), oklch(0.8 0.28 85)) right top 5px / 1px calc(100% - 10px) no-repeat",
      ].join(", "),
    },
  },
}

/* ── Neon bar child spans ── */

function NeonBars({ variant }: { variant: ButtonVariant }) {
  const config = neonBarConfigs[variant]
  if (!config) return null

  return (
    <>
      <span
        className="pointer-events-none absolute z-[-1] opacity-0 transition-opacity duration-200 group-hover/btn:opacity-100 group-active/btn:opacity-0 group-disabled/btn:hidden"
        style={config.outer}
        aria-hidden
      />
      <span
        className="pointer-events-none absolute z-[-1] opacity-0 transition-opacity duration-200 group-hover/btn:opacity-100 group-active/btn:opacity-0 group-disabled/btn:hidden"
        style={config.inner}
        aria-hidden
      />
    </>
  )
}

/* ── Compute inline style for current interaction state ── */

function computeStyle(
  variant: ButtonVariant,
  disabled: boolean,
  hovered: boolean,
  active: boolean,
  styleProp?: CSSProperties,
): CSSProperties {
  const base = variantBaseStyles[variant]
  if (!base && !styleProp) return {}

  return {
    ...base,
    ...(disabled ? variantDisabledStyles[variant] : {}),
    ...(!disabled && hovered && !active ? variantHoverStyles[variant] : {}),
    ...(!disabled && active ? variantActiveStyles[variant] : {}),
    ...styleProp,
  }
}

/* ── Whether this variant needs JS-driven hover/active style switching ── */

const NEEDS_STATE_STYLES = new Set<ButtonVariant>([
  "default",
  "secondary",
  "gold",
])

/* ── Button component ── */

function Button({
  className,
  variant,
  size,
  asChild = false,
  style,
  children,
  onPointerEnter,
  onPointerLeave,
  onPointerDown,
  onPointerUp,
  ...props
}: ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"
  const resolved = variant ?? "default"
  const isDisabled = !!props.disabled
  const needsState = NEEDS_STATE_STYLES.has(resolved)

  const [hovered, setHovered] = useState(false)
  const [active, setActive] = useState(false)

  const computedStyle = needsState
    ? computeStyle(resolved, isDisabled, hovered, active, style)
    : style

  const hasNeonBars =
    resolved === "default" || resolved === "secondary" || resolved === "gold"

  const stateHandlers = needsState
    ? {
        onPointerEnter: (e: PointerEvent<HTMLButtonElement>) => {
          setHovered(true)
          onPointerEnter?.(e)
        },
        onPointerLeave: (e: PointerEvent<HTMLButtonElement>) => {
          setHovered(false)
          setActive(false)
          onPointerLeave?.(e)
        },
        onPointerDown: (e: PointerEvent<HTMLButtonElement>) => {
          setActive(true)
          onPointerDown?.(e)
        },
        onPointerUp: (e: PointerEvent<HTMLButtonElement>) => {
          setActive(false)
          onPointerUp?.(e)
        },
      }
    : { onPointerEnter, onPointerLeave, onPointerDown, onPointerUp }

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      style={computedStyle}
      {...stateHandlers}
      {...props}
    >
      {hasNeonBars && <NeonBars variant={resolved} />}
      {asChild ? <Slottable>{children}</Slottable> : children}
    </Comp>
  )
}

export { Button, buttonVariants }
